import { Directive, input, output } from '@angular/core';


/**
 * Makes a clickable table row keyboard-accessible: focusable via Tab, activated by Enter/Space.
 * Events bubbling from nested controls (e.g. delete icon) are ignored.
 *
 * @example
 * <tr mat-row uiRowLink (rowActivate)="open(row)"></tr>
 */
@Directive({
    selector: 'tr[uiRowLink]',
    host: {
        '[attr.tabindex]': 'rowLinkDisabled() ? null : 0',
        '(click)': 'activate($event)',
        '(keydown.enter)': 'activate($event)',
        '(keydown.space)': 'activate($event)',
    },
})
export class RowLinkDirective {
    /**
     * Disable row activation and remove it from the tab order
     */
    public readonly rowLinkDisabled = input<boolean>(false);
    /**
     * Row activated by click, Enter or Space
     */
    public readonly rowActivate = output<void>();

    protected activate(event: Event): void {
        if (this.rowLinkDisabled() || event.target !== event.currentTarget && this.#isInteractive(event.target as HTMLElement, event.currentTarget as HTMLElement)) {
            return;
        }

        if (event.type === 'keydown') {
            event.preventDefault();
        }

        this.rowActivate.emit();
    }

    /**
     * True if the event came from a focusable/interactive element inside the row
     */
    #isInteractive(target: HTMLElement, row: HTMLElement): boolean {
        const interactive: HTMLElement | null = target.closest('a, button, input, select, textarea, [role="button"], [tabindex]');

        return !!interactive && interactive !== row && row.contains(interactive);
    }
}
